import React, { useState, useEffect } from "react";
import axios from "axios";
import styles from "./style/PacoteForm.module.css";

type Props = {
    BASE_URL: string;
    token: string | null;
    empresaId: number;
    pacote?: any;
    onSave: (p: any) => void;
    onCancel: () => void;
};

const idiomaOptions = [
    { value: "english", label: "English" },
    { value: "italian", label: "Italian" },
    { value: "french", label: "French" },
    { value: "german", label: "German" },
    { value: "portuguese", label: "Portuguese" },
    { value: "russian", label: "Russian" },
    { value: "hebrew", label: "Hebrew" },
    { value: "mandarin", label: "Mandarin" },
    { value: "japanese", label: "Japanese" },
    { value: "ukrainian", label: "Ukrainian" },
    // adicionais
    { value: "spanish", label: "Spanish" },
    { value: "dutch", label: "Dutch" },
    { value: "swedish", label: "Swedish" },
    { value: "norwegian", label: "Norwegian" },
    { value: "danish", label: "Danish" },
    { value: "polish", label: "Polish" },
    { value: "greek", label: "Greek" },
    { value: "turkish", label: "Turkish" },
    { value: "czech", label: "Czech" },
    { value: "hungarian", label: "Hungarian" },
    { value: "romanian", label: "Romanian" },
    { value: "finnish", label: "Finnish" },
    { value: "slovak", label: "Slovak" },
    { value: "bulgarian", label: "Bulgarian" },
    { value: "croatian", label: "Croatian" },
    { value: "irish", label: "Irish" },
    { value: "welsh", label: "Welsh" },
    { value: "scottish_gaelic", label: "Scottish Gaelic" },
    { value: "basque", label: "Basque" },
    { value: "catalan", label: "Catalan" },
    { value: "galician", label: "Galician" },
];

const tipoDiferencialOptions = [
    { value: "AGUA", label: "Água" },
    { value: "CERVEJA", label: "Cerveja" },
    { value: "VINHO", label: "Vinho" },
    { value: "CHAMPAGNE", label: "Champagne" },
    { value: "OUTRO", label: "Outro" },
];


const publicoLabels: Record<string, string> = {
    FAMILIA: "Family",
    AMIGOS: "Friends",
    EMPRESARIAL: "Business",
    TURISTA: "Tourists",
};

type MotoristaOption = {
    id: number;
    nome: string;
    fotoUrl?: string | null;
    veiculoId?: number;
    veiculoModelo?: string;
    veiculoPlaca?: string;
    idiomas: string[];
};

const PacoteForm = ({ BASE_URL, token, empresaId, pacote, onSave, onCancel }: Props) => {
    const [titulo, setTitulo] = useState("");
    const [descricao, setDescricao] = useState("");
    const [precoBase, setPrecoBase] = useState<number | "">("");
    const [duracao, setDuracao] = useState<number | "">("");
    const [idadeMinima, setIdadeMinima] = useState<number | "">("");
    const [publicoAlvo, setPublicoAlvo] = useState("");
    const [idiomas, setIdiomas] = useState<string[]>([]);
    const [fotos, setFotos] = useState<File[]>([]);
    const [videos, setVideos] = useState<File[]>([]);
    const [diferenciais, setDiferenciais] = useState<
        { tipo: string; descricaoCustomizada?: string; quantidade: number }[]
    >([]);
    const [pontos, setPontos] = useState<string[]>([]);
    const [novoPonto, setNovoPonto] = useState("");
    const [pontosImportantes, setPontosImportantes] = useState<string[]>([]);
    const [novoPontoImportante, setNovoPontoImportante] = useState("");
    const [motoristas, setMotoristas] = useState<MotoristaOption[]>([]);
    const [motoristaSelecionadosIds, setMotoristaSelecionadosIds] = useState<number[]>(pacote?.motoristaIds || []);

    const [fotosExistentes, setFotosExistentes] = useState<string[]>([]);
    const [videosExistentes, setVideosExistentes] = useState<string[]>([]);

    useEffect(() => {
        if (pacote) {
            setTitulo(pacote.titulo || "");
            setDescricao(pacote.descricao || "");
            setPrecoBase(pacote.precoBase ?? "");
            setDuracao(pacote.duracaoEmMinutos ?? "");
            setIdadeMinima(pacote.idadeMinima ?? "");
            setPublicoAlvo(pacote.publicoAlvo || "");
            setIdiomas(pacote.idiomas || []);
            setDiferenciais(pacote.diferenciais || []);
            setPontos(pacote.pontosDeInteresse || []);

            // 🔹 inicializa com as mídias existentes
            setFotosExistentes(pacote.fotoUrls || []);
            setVideosExistentes(pacote.videoUrls || []);
        }
    }, [pacote]);

    // State para serviços
    const [servicos, setServicos] = useState<{ id: number; nome: string; icone?: string }[]>([]);
    const [servicoSelecionadoId, setServicoSelecionadoId] = useState<number | null>(pacote?.servicoId || null);
    const [servicoSelecionado, setServicoSelecionado] = useState<{ id: number; nome: string; icone?: string } | null>(null);

    // useEffect para carregar serviços
    useEffect(() => {
        const fetchServicos = async () => {
            if (!token) return;
            try {
                const res = await axios.get(`${BASE_URL}/servicos`, {
                    headers: { Authorization: `Bearer ${token}` },
                });
                setServicos(res.data);
            } catch (err) {
                console.error("Erro ao carregar serviços:", err);
            }
        };
        fetchServicos();
    }, [BASE_URL, token]);

    useEffect(() => {
        const selecionado = servicos.find(s => s.id === servicoSelecionadoId) || null;
        setServicoSelecionado(selecionado);

        // Só mantém motoristas se for Tuk Tuk
        if (selecionado?.nome !== "Tuk Tuk") {
            setMotoristaSelecionadosIds([]); // limpa a lista
        }
    }, [servicoSelecionadoId, servicos]);

    useEffect(() => {
        // Carrega motoristas aprovados
        const fetchMotoristas = async () => {
            if (!token) return;
            try {
                const res = await axios.get<MotoristaOption[]>(`${BASE_URL}/empresas/${empresaId}/motoristas-aprovados`, {
                    headers: { Authorization: `Bearer ${token}` },
                });
                setMotoristas(res.data);
            } catch (err) {
                console.error("Erro ao carregar motoristas:", err);
            }
        };
        fetchMotoristas();
    }, [BASE_URL, empresaId, token]);

    useEffect(() => {
        if (servicoSelecionado?.nome === "Tuk Tuk" && motoristaSelecionadosIds.length > 0) {
            const selecionados = motoristas.filter(m => motoristaSelecionadosIds.includes(m.id));
            // Une todos os idiomas dos motoristas selecionados
            const idiomasUnicos = Array.from(new Set(selecionados.flatMap(m => m.idiomas || [])));
            setIdiomas(idiomasUnicos);
        } else if (!pacote?.id) {
            setIdiomas([]);
        }
    }, [motoristaSelecionadosIds, servicoSelecionado, motoristas, pacote?.id]);

    const handleUploadFotos = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.files) {
            setFotos([...fotos, ...Array.from(e.target.files)]);
        }
    };

    const handleRemoveFoto = (index: number) => {
        setFotos(fotos.filter((_, i) => i !== index));
    };

    const handleUploadVideos = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.files) {
            setVideos([...videos, ...Array.from(e.target.files)]);
        }
    };

    const handleRemoveVideo = (index: number) => {
        setVideos(videos.filter((_, i) => i !== index));
    };

    const handleAddDiferencial = () => {
        setDiferenciais([...diferenciais, { tipo: "AGUA", quantidade: 1 }]);
    };

    const handleChangeDiferencial = (
        index: number,
        field: "tipo" | "quantidade" | "descricaoCustomizada",
        value: string | number
    ) => {
        const newDif = [...diferenciais];
        // @ts-expect-error simplificação de tipos
        newDif[index][field] = value;
        setDiferenciais(newDif);
    };

    const handleRemoveDiferencial = (index: number) => {
        setDiferenciais(diferenciais.filter((_, i) => i !== index));
    };

    const handleAddPonto = () => {
        if (novoPonto.trim()) {
            setPontos([...pontos, novoPonto.trim()]);
            setNovoPonto("");
        }
    };

    const handleRemovePonto = (ponto: string) => {
        setPontos(pontos.filter((p) => p !== ponto));
    };

    const handleAddPontoImportante = () => {
        if (novoPontoImportante.trim()) {
            setPontosImportantes([...pontosImportantes, novoPontoImportante.trim()]);
            setNovoPontoImportante("");
        }
    };

    const handleRemovePontoImportante = (ponto: string) => {
        setPontosImportantes(pontosImportantes.filter(p => p !== ponto));
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        try {
            // 1️⃣ Upload das novas fotos
            const novasFotosIds: number[] = [];
            for (const file of fotos) {
                const formData = new FormData();
                formData.append("file", file);
                const res = await axios.post(`${BASE_URL}/uploads`, formData, {
                    headers: { Authorization: `Bearer ${token}`, "Content-Type": "multipart/form-data" },
                });
                novasFotosIds.push(res.data.id);
            }

            // 2️⃣ Upload dos novos vídeos
            const novosVideosIds: number[] = [];
            for (const file of videos) {
                const formData = new FormData();
                formData.append("file", file);
                const res = await axios.post(`${BASE_URL}/uploads`, formData, {
                    headers: { Authorization: `Bearer ${token}`, "Content-Type": "multipart/form-data" },
                });
                novosVideosIds.push(res.data.id);
            }

            // 3️⃣ Conciliar: existentes + novos
            const fotoIds = [
                ...fotosExistentes.map((url) => {
                    const parts = url.split("/");
                    return Number(parts[parts.length - 1]); // supõe que a URL termina com o ID
                }),
                ...novasFotosIds,
            ];

            const videoIds = [
                ...videosExistentes.map((url) => {
                    const parts = url.split("/");
                    return Number(parts[parts.length - 1]);
                }),
                ...novosVideosIds,
            ];

            // 4️⃣ Montar DTO final
            const dto = {
                titulo,
                descricao,
                precoBase: Number(precoBase),
                duracaoEmMinutos: duracao ? Number(duracao) : null,
                idadeMinima: idadeMinima ? Number(idadeMinima) : null,
                publicoAlvo,
                idiomas,
                diferenciais,
                pontosDeInteresse: pontos,
                pontosImportantes,
                fotoIds,
                videoIds,
                motoristaIds: motoristaSelecionadosIds,
                servicoId: servicoSelecionadoId,
                empresaId,
            };

            // 5️⃣ Criar ou atualizar
            let res;
            if (pacote?.id) {
                res = await axios.put(`${BASE_URL}/pacotes/${pacote.id}`, dto, {
                    headers: { Authorization: `Bearer ${token}` },
                });
            } else {
                res = await axios.post(`${BASE_URL}/pacotes`, dto, {
                    headers: { Authorization: `Bearer ${token}` },
                });
            }

            onSave(res.data);
            resetForm();
        } catch (err) {
            console.error("Erro ao salvar pacote:", err);
        }
    };

    const resetForm = () => {
        setTitulo("");
        setDescricao("");
        setPrecoBase("");
        setDuracao("");
        setIdadeMinima("");
        setPublicoAlvo("");
        setIdiomas([]);
        setFotos([]);
        setVideos([]);
        setDiferenciais([]);
        setPontos([]);
        setPontosImportantes([]); // ✅ novo
    };

    return (
        <form onSubmit={handleSubmit} className={styles.formSection}>
            {/* Título */}
            <div className="alert alert-warning mb-3">
                Lembre-se: ao publicar ou editar um pacote, ele passará por uma análise antes de ser disponibilizado para todos os usuários da plataforma.
            </div>
            <div className={styles.formSectionRow}>
                <div className={styles.formGroupWide}>
                    <label>Name of the tour</label>
                    <input
                        type="text"
                        placeholder="Name of the tour"
                        value={titulo}
                        onChange={e => setTitulo(e.target.value)}
                        required
                    />
                </div>
            </div>

            {/* Serviço */}
            <div className={styles.formGroup}>
                <label>Service</label>
                <select
                    value={servicoSelecionadoId ?? ""}
                    onChange={e => setServicoSelecionadoId(Number(e.target.value))}
                    required
                >
                    <option value="">Select a service</option>
                    {servicos.map(s => (
                        <option key={s.id} value={s.id}>
                            {s.icone ? `${s.icone} ` : ""}{s.nome}
                        </option>
                    ))}
                </select>
            </div>

            {/* Motorista — só aparece se o serviço for Tuk Tuk */}
            {servicoSelecionado?.nome === "Tuk Tuk" && (
                <div className={styles.formGroup}>
                    <label>Drivers</label>
                    <div className={styles.checkboxContainer}>
                        {motoristas.map(m => (
                            <label key={m.id} className={styles.checkboxLabel}>
                                <input
                                    type="checkbox"
                                    value={m.id}
                                    checked={motoristaSelecionadosIds.includes(m.id)}
                                    onChange={e => {
                                        const id = m.id;
                                        if (e.target.checked) {
                                            setMotoristaSelecionadosIds(prev => [...prev, id]);
                                        } else {
                                            setMotoristaSelecionadosIds(prev => prev.filter(mid => mid !== id));
                                        }
                                    }}
                                />
                                {m.nome} {m.veiculoModelo ? `- ${m.veiculoModelo} (${m.veiculoPlaca})` : ""}
                            </label>
                        ))}
                    </div>
                </div>
            )}

            {/* Preço, Duração, Idade mínima e Público */}
            <div className={styles.formSectionRow}>
                {[
                    { label: "Price", value: precoBase, setter: setPrecoBase, type: "number", placeholder: "Price", required: true },
                    { label: "Duration (min)", value: duracao, setter: setDuracao, type: "number", placeholder: "Duration" },
                    { label: "Minimum age", value: idadeMinima, setter: setIdadeMinima, type: "number", placeholder: "Minimum age" }
                ].map((f, i) => (
                    <div key={i} className={styles.formGroup}>
                        <label>{f.label}</label>
                        <input
                            type={f.type}
                            placeholder={f.placeholder}
                            value={f.value}
                            onChange={e => f.setter(Number(e.target.value))}
                            {...(f.required ? { required: true } : {})}
                        />
                    </div>
                ))}
                <div className={styles.formGroup}>
                    <label>Target audience</label>
                    <select value={publicoAlvo} onChange={e => setPublicoAlvo(e.target.value)}>
                        <option value="">Select target audience</option>
                        {Object.entries(publicoLabels).map(([key, label]) => (
                            <option key={key} value={key}>{label}</option>
                        ))}
                    </select>
                </div>
            </div>

            {/* Descrição */}
            <div className={styles.formSection}>
                <div className={styles.formGroup}>
                    <label>Description</label>
                    <textarea
                        placeholder="Description"
                        value={descricao}
                        onChange={e => setDescricao(e.target.value)}
                        required
                    />
                </div>
            </div>

            {/* Idiomas */}
            <div className={styles.formSection}>
                <label>Languages ​​offered</label>
                <div className={styles.checkboxContainer}>
                    {servicoSelecionado?.nome === "Tuk Tuk" && motoristaSelecionadosIds ? (
                        // Idiomas lockados do motorista
                        idiomas.map(idioma => (
                            <label key={idioma} className={styles.checkboxLabel}>
                                <input type="checkbox" checked value={idioma} readOnly />
                                {idiomaOptions.find(opt => opt.value === idioma)?.label || idioma}
                            </label>
                        ))
                    ) : (
                        // Idiomas selecionáveis manualmente
                        idiomaOptions.map(idioma => (
                            <label key={idioma.value} className={styles.checkboxLabel}>
                                <input
                                    type="checkbox"
                                    value={idioma.value}
                                    checked={idiomas.includes(idioma.value)}
                                    onChange={e =>
                                        setIdiomas(prev =>
                                            e.target.checked
                                                ? [...prev, idioma.value]
                                                : prev.filter(i => i !== idioma.value)
                                        )
                                    }
                                />
                                {idioma.label}
                            </label>
                        ))
                    )}
                </div>
            </div>

            {/* Diferenciais */}
            <div className={styles.formSection}>
                <label>Differentials</label>
                {diferenciais.map((dif, i) => (
                    <div key={i} className={styles.diferencialCard}>
                        <select
                            value={dif.tipo}
                            onChange={e => handleChangeDiferencial(i, "tipo", e.target.value)}
                        >
                            {tipoDiferencialOptions.map(opt => (
                                <option key={opt.value} value={opt.value}>{opt.label}</option>
                            ))}
                        </select>

                        {dif.tipo === "OUTRO" && (
                            <input
                                type="text"
                                placeholder="Descrição"
                                value={dif.descricaoCustomizada || ""}
                                onChange={e => handleChangeDiferencial(i, "descricaoCustomizada", e.target.value)}
                            />
                        )}

                        <input
                            type="number"
                            placeholder="Qtd"
                            value={dif.quantidade}
                            onChange={e => handleChangeDiferencial(i, "quantidade", Number(e.target.value))}
                        />

                        <button type="button" className={styles.removeBtn} onClick={() => handleRemoveDiferencial(i)}>❌</button>
                    </div>
                ))}

                <button type="button" className={styles.addButton} onClick={handleAddDiferencial}>
                    + Add differential
                </button>
            </div>

            {/* Pontos de Interesse */}
            <div className={styles.formSection}>
                <label>Points of Interest</label>
                <small className={styles.helperText}>
                    These are places that are not necessarily part of the tour but are worth visiting.
                </small>
                <div className={styles.formSectionRow}>
                    <input
                        type="text"
                        placeholder="Add point of interest"
                        value={novoPonto}
                        onChange={e => setNovoPonto(e.target.value)}
                    />
                    <button type="button" className={styles.addButton} onClick={handleAddPonto}>
                        Add
                    </button>
                </div>
                <div className={styles.tagsContainer}>
                    {pontos.map(p => (
                        <span key={p} className={styles.tag}>
                            {p} <button type="button" onClick={() => handleRemovePonto(p)}>❌</button>
                        </span>
                    ))}
                </div>
            </div>

            {/* Pontos Importantes */}
            <div className={styles.formSection}>
                <label>Important Points</label>
                <small className={styles.helperText}>
                    These are the actual stops that are part of the tour itinerary.
                </small>
                <div className={styles.formSectionRow}>
                    <input
                        type="text"
                        placeholder="Add important point"
                        value={novoPontoImportante}
                        onChange={e => setNovoPontoImportante(e.target.value)}
                    />
                    <button type="button" className={styles.addButton} onClick={handleAddPontoImportante}>
                        Add
                    </button>
                </div>
                <div className={styles.tagsContainer}>
                    {pontosImportantes.map(p => (
                        <span key={p} className={styles.tag}>
                            {p} <button type="button" onClick={() => handleRemovePontoImportante(p)}>❌</button>
                        </span>
                    ))}
                </div>
            </div>

            {[
                { label: "Fotos", files: fotos, urls: fotosExistentes, onUpload: handleUploadFotos, isVideo: false },
                { label: "Vídeos", files: videos, urls: videosExistentes, onUpload: handleUploadVideos, isVideo: true }
            ].map((u, i) => (
                <div key={i} className={styles.formSection}>
                    <label>{u.label}</label>
                    <input type="file" multiple accept={u.isVideo ? "video/*" : "image/*"} onChange={u.onUpload} />

                    <div className={u.isVideo ? styles.videoPreview : styles.fotoPreview}>
                        {/* 🔹 renderiza URLs já existentes */}
                        {u.urls.map((url, idx) => (
                            <div key={`exist-${idx}`} className={u.isVideo ? styles.videoCard : styles.fotoCard}>
                                {u.isVideo ? <video controls src={`${BASE_URL}${url}`} /> : <img src={`${BASE_URL}${url}`} alt="Preview" />}
                                <button
                                    type="button"
                                    className={styles.removeFotoBtn}
                                    onClick={() => {
                                        // remove mídia existente
                                        u.isVideo
                                            ? setVideosExistentes(u.urls.filter((_, i) => i !== idx))
                                            : setFotosExistentes(u.urls.filter((_, i) => i !== idx));
                                    }}
                                >
                                    ❌
                                </button>
                            </div>
                        ))}

                        {/* 🔹 renderiza arquivos novos */}
                        {u.files.map((file, idx) => {
                            const url = URL.createObjectURL(file);
                            return (
                                <div key={`new-${idx}`} className={u.isVideo ? styles.videoCard : styles.fotoCard}>
                                    {u.isVideo ? <video controls src={url} /> : <img src={url} alt="Preview" />}
                                    <button
                                        type="button"
                                        className={styles.removeFotoBtn}
                                        onClick={() => {
                                            u.isVideo ? handleRemoveVideo(idx) : handleRemoveFoto(idx);
                                            URL.revokeObjectURL(url);
                                        }}
                                    >
                                        ❌
                                    </button>
                                </div>
                            );
                        })}
                    </div>
                </div>
            ))}

            {/* Botões */}
            <div className={styles.formActions}>
                <button type="submit" className={styles.addButton}>
                    {pacote ? "Atualizar Pacote" : "Cadastrar Pacote"}
                </button>
                {pacote && (
                    <button type="button" onClick={onCancel} className={styles.cancelButton}>
                        Cancelar
                    </button>
                )}
            </div>
        </form>
    );

};

export default PacoteForm;
